import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { MenuCategoryProxy } from "@me-team/host/main/graphql/types";
import { DeepPartial } from "@apollo/client/utilities";
import { TFunction } from "i18next";
import { MenuItemFormData } from "../../DishUpdatePage/DishUpdatePage";

interface IPickedCategoryNames {
    id: number;
    name: string;
    parentName: string | null;
}

type UseCreateDishParamsOptions = {
    categoryListData?: DeepPartial<MenuCategoryProxy[]>;
    t: TFunction;
};

type MenuContentInfoState = {
    menuContentInfo?: {
        categoryId?: number | null;
        childCategoryId?: number | null;
        menuId?: number | null;
    };
};

export const useCreateDishParams = (options?: UseCreateDishParamsOptions) => {
    const location = useLocation();
    const state = location.state as MenuContentInfoState | null;

    return useMemo(() => {
        const categoryId = state?.menuContentInfo?.categoryId ?? null;
        const childCategoryId = state?.menuContentInfo?.childCategoryId ?? null;
        const menuId = state?.menuContentInfo?.menuId ?? null;

        const hasCategories = !!(categoryId || childCategoryId);

        let pickedCategories: IPickedCategoryNames[] = [];
        let initFormValues: MenuItemFormData = {
            name: "",
            types: [],
            categories: [],
            description: "",
            composition: "",
            unit: null,
            portions: [
                {
                    name: options?.t("Standard portion"),
                    volume: null,
                    price: null,
                },
            ],
            cookingTime: "",
            isLongCookingTime: false,
            markers: [],
            allergens: [],
            supplements: [],
            image: null,
            isUpdateImage: false,
            deleted: false,
        };

        if (options?.categoryListData && categoryId) {
            const category = options.categoryListData.find((c) => c?.id === categoryId);

            if (category) {
                const child =
                    childCategoryId != null
                        ? category.childCategories?.find((cc) => cc?.id === childCategoryId)
                        : undefined;

                if (child) {
                    pickedCategories = [
                        {
                            id: child.id as number,
                            name: child.name as string,
                            parentName: (category.name as string) ?? null,
                        },
                    ];
                } else {
                    pickedCategories = [
                        {
                            id: category.id as number,
                            name: category.name as string,
                            parentName: null,
                        },
                    ];
                }
            }

            const categoryIdForInitValue = [childCategoryId ? childCategoryId : categoryId];

            initFormValues = {
                ...initFormValues,
                categories: categoryIdForInitValue,
            };
        }

        return {
            pickedCategories,
            hasCategories,
            menuId,
            categoryId,
            childCategoryId,
            initFormValues,
        };
    }, [state?.menuContentInfo, options]);
};
